import { HTMLAttributes, useEffect, useState } from 'react'
import Link from 'next/link'

import Colophon from '@/components/base/colophon'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Logo from '@/components/base/logo'
import ScrollDown from '@/components/base/scroll-down'
import Socials from '@/components/base/socials'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import SectionContacts from '@/components/sections/contact-form'
import { useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useTheme } from '@/contexts/theme'
import { useLayoutStore } from '@/store/layout'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    showInline: boolean
  }>

const Footer = ({ showInline, className, ...props }: FooterProps) => {
  const { menu } = useStore()
  const { theme } = useTheme()
  const [footerOpen, setFooterOpen] = useState(false)
  const footerTheme = useLayoutStore((state) => state.footerTheme)
  const srollDownHidden = useLayoutStore((state) => state.srollDownHidden)
  const setLayoutState = useLayoutStore((state) => state.setState)

  useEffect(() => {
    setLayoutState({ footerTheme: theme.header })

    const unsubscribe = useLenisStore.subscribe(
      (state) => state.scroll,
      () => setFooterOpen(false)
    )

    return () => {
      unsubscribe()
    }
  }, [theme, setLayoutState])

  return (
    <>
      <SectionContacts />

      {showInline ? (
        <div
          className={cn('fixed bottom-0 left-0 right-0 z-inline-footer', {
            'text-text-primary-dark': footerTheme === 'light' || footerOpen,
            'text-text-accent-light': footerTheme === 'dark' && !footerOpen
          })}
        >
          <ScrollDown
            className={cn(
              'absolute bottom-[9.8rem] left-margin',
              'md:bottom-[3.1rem] md:left-1/2 lg:-translate-x-1/2',
              'transition-opacity duration-300 ease-out-cubic',
              {
                'text-text-primary-dark': theme.header === 'light',
                'text-text-accent-light': theme.header === 'dark',
                'opacity-0': srollDownHidden
              }
            )}
          />

          <div
            className={cn(
              'relative px-offset py-spacing-sm',
              'md:flex md:items-center md:justify-between',
              'transition-colors duration-300 ease-out-cubic',
              {
                'bg-surface-accent-light': footerOpen
              }
            )}
          >
            <div className="relative flex items-center justify-between md:order-2">
              <button
                onClick={() => setFooterOpen((footerOpen) => !footerOpen)}
                className="flex rounded-radius-minimal border-2 p-[1.4rem] md:hidden"
              >
                <span className="sr-only">open footer</span>
                <SVGIcon
                  symbol="arrow-icon"
                  className={cn(
                    'h-[1.6rem] w-[1.6rem] fill-current',
                    'transition-transform duration-500 ease-out-cubic',
                    {
                      'rotate-180': footerOpen
                    }
                  )}
                />
              </button>

              <Socials />
            </div>
            <div
              className={cn(
                'relative z-20 grid md:order-1 md:grid-rows-none',
                'transition-all duration-500 ease-out-cubic',
                {
                  'grid-rows-[0fr]': !footerOpen,
                  'grid-rows-[1fr]': footerOpen
                }
              )}
            >
              <div className="overflow-hidden md:overflow-visible">
                <div className="pb-[0.2em] pt-spacing-lg md:p-0">
                  <Colophon />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <footer
        className={cn(
          'relative z-footer',
          'pb-margin pt-spacing-md md:pb-spacing-lg',
          'bg-surface-accent-light text-text-primary-dark',
          className
        )}
        {...props}
      >
        <Container className="max-w-full px-offset">
          <Grid className="gap-y-spacing-sm md:gap-y-spacing-lg">
            <div className={cn('col-span-2 md:col-span-6 lg:col-span-12', 'grid gap-y-spacing-lg')}>
              <div className="inline-flex">
                <Link href="/" scroll={false}>
                  <Logo className="fill-current" />
                </Link>
              </div>

              <div
                className={cn(
                  'grid gap-y-spacing-md',
                  'md:flex md:items-end md:justify-between md:gap-y-0'
                )}
              >
                <div className="grid shrink-0 grow gap-y-spacing-sm">
                  {menu?.main_menu_01?.menu.map((item, index) => (
                    <div key={`main_menu_01_${item.id}_${index}`} className="flex">
                      <Link
                        href={item.url}
                        scroll={false}
                        target={item.target}
                        className="group inline-flex"
                      >
                        <Typography variant="h6" mask>
                          {item.title}
                        </Typography>
                      </Link>
                    </div>
                  ))}
                </div>

                <div className="grid shrink-0 grow gap-y-spacing-sm">
                  {menu?.main_menu_02?.menu.map((item, index) => (
                    <div key={`main_menu_02_${item.id}_${index}`} className="flex">
                      <Link
                        href={item.url}
                        scroll={false}
                        target={item.target}
                        className="group inline-flex"
                      >
                        <Typography variant="large" mask className="font-medium">
                          {item.title}
                        </Typography>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              className={cn(
                'col-span-2 md:col-span-6 lg:col-span-12',
                'grid gap-y-spacing-md',
                'md:flex md:items-center md:justify-between md:gap-y-0'
              )}
            >
              <Socials className="md:order-2" />

              <Colophon className="opacity-50" />
            </div>
          </Grid>
        </Container>
      </footer>
    </>
  )
}

export default Footer
